<template>
    <div>
        <Head title="Tableau de bord KPIs" />

        <!-- KPIs globaux -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-indigo-600">{{ globalKpis.total_tickets }}</div>
                <div class="text-xs text-gray-500 mt-1">Tickets total</div>
            </div>
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-orange-500">{{ globalKpis.open_tickets }}</div>
                <div class="text-xs text-gray-500 mt-1">Tickets ouverts</div>
            </div>
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-green-600">{{ globalKpis.closed_this_month }}</div>
                <div class="text-xs text-gray-500 mt-1">Clôturés ce mois</div>
            </div>
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-blue-600">{{ globalKpis.avg_resolution_hours }}h</div>
                <div class="text-xs text-gray-500 mt-1">Délai moyen résolution</div>
            </div>
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-purple-600">{{ globalKpis.total_equipment }}</div>
                <div class="text-xs text-gray-500 mt-1">Équipements</div>
            </div>
            <div class="bg-white rounded-md shadow p-4 text-center">
                <div class="text-3xl font-bold text-gray-600">{{ globalKpis.total_buildings }}</div>
                <div class="text-xs text-gray-500 mt-1">Bâtiments</div>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <!-- Charge par département -->
            <div class="bg-white rounded-md shadow">
                <div class="px-6 py-4 border-b font-semibold text-gray-700">Charge par département</div>
                <div class="p-4">
                    <div v-if="ticketsByDept.length === 0" class="text-gray-400 text-sm py-4 text-center">Aucune donnée.</div>
                    <div v-for="d in ticketsByDept" :key="d.id" class="mb-3">
                        <div class="flex justify-between text-sm mb-1">
                            <span class="font-medium">{{ d.name || 'Non assigné' }}</span>
                            <span class="text-gray-500">{{ d.open }} ouverts / {{ d.total }} total</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                            <div class="h-2 rounded-full bg-indigo-500" :style="{ width: barWidth(d.total) }"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Délai moyen de résolution -->
            <div class="bg-white rounded-md shadow">
                <div class="px-6 py-4 border-b font-semibold text-gray-700">Délai moyen de résolution (heures)</div>
                <table class="min-w-full text-sm">
                    <tr class="text-left font-semibold text-gray-500">
                        <th class="px-6 py-2">Département</th>
                        <th class="px-6 py-2 text-right">Moy.</th>
                        <th class="px-6 py-2 text-right">Min</th>
                        <th class="px-6 py-2 text-right">Max</th>
                        <th class="px-6 py-2 text-right">Tickets</th>
                    </tr>
                    <tr v-for="r in resolutionByDept" :key="r.department" class="border-t hover:bg-gray-50">
                        <td class="px-6 py-2">{{ r.department }}</td>
                        <td class="px-6 py-2 text-right font-semibold" :class="resolutionClass(r.avg_hours)">{{ r.avg_hours }}h</td>
                        <td class="px-6 py-2 text-right text-gray-500">{{ r.min_hours }}h</td>
                        <td class="px-6 py-2 text-right text-gray-500">{{ r.max_hours }}h</td>
                        <td class="px-6 py-2 text-right text-gray-400">{{ r.total }}</td>
                    </tr>
                    <tr v-if="resolutionByDept.length === 0">
                        <td class="px-6 py-3 text-gray-400" colspan="5">Aucune donnée de résolution.</td>
                    </tr>
                </table>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <!-- Tickets en retard par département -->
            <div class="bg-white rounded-md shadow">
                <div class="px-6 py-4 border-b font-semibold text-gray-700 flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                    Tickets ouverts depuis + de 7 jours
                </div>
                <div class="p-4">
                    <div v-if="overdueTickets.length === 0" class="text-gray-400 text-sm text-center py-4">Aucun ticket en retard ✓</div>
                    <div v-for="o in overdueTickets" :key="o.department" class="flex justify-between items-center py-2 border-b last:border-0">
                        <span class="text-sm">{{ o.department }}</span>
                        <span class="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">{{ o.total }}</span>
                    </div>
                </div>
            </div>

            <!-- Délai de première réponse -->
            <div class="bg-white rounded-md shadow">
                <div class="px-6 py-4 border-b font-semibold text-gray-700">Délai moyen de 1ère réponse (heures)</div>
                <table class="min-w-full text-sm">
                    <tr class="text-left font-semibold text-gray-500">
                        <th class="px-6 py-2">Département</th>
                        <th class="px-6 py-2 text-right">Moy. heures</th>
                    </tr>
                    <tr v-for="r in responseByDept" :key="r.department" class="border-t hover:bg-gray-50">
                        <td class="px-6 py-2">{{ r.department }}</td>
                        <td class="px-6 py-2 text-right font-semibold" :class="resolutionClass(r.avg_hours)">{{ r.avg_hours }}h</td>
                    </tr>
                    <tr v-if="responseByDept.length === 0">
                        <td class="px-6 py-3 text-gray-400" colspan="2">Aucune donnée.</td>
                    </tr>
                </table>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <!-- Évolution mensuelle -->
            <div class="bg-white rounded-md shadow">
                <div class="px-6 py-4 border-b font-semibold text-gray-700">Évolution mensuelle (12 mois)</div>
                <div class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <tr class="text-left font-semibold text-gray-500">
                            <th class="px-4 py-2">Mois</th>
                            <th class="px-4 py-2 text-right text-blue-500">Ouverts</th>
                            <th class="px-4 py-2 text-right text-green-500">Clôturés</th>
                        </tr>
                        <tr v-for="m in monthlyTrend" :key="m.label" class="border-t hover:bg-gray-50">
                            <td class="px-4 py-2 text-gray-700">{{ m.label }}</td>
                            <td class="px-4 py-2 text-right font-semibold text-blue-600">{{ m.opened }}</td>
                            <td class="px-4 py-2 text-right font-semibold text-green-600">{{ m.closed }}</td>
                        </tr>
                    </table>
                </div>
            </div>

            <!-- Équipements & Maintenance -->
            <div class="space-y-4">
                <!-- Équipements par statut -->
                <div class="bg-white rounded-md shadow">
                    <div class="px-6 py-4 border-b font-semibold text-gray-700">Équipements par statut</div>
                    <div class="p-4 grid grid-cols-2 gap-3">
                        <div v-for="s in equipmentByStatus" :key="s.status" class="text-center p-3 rounded-lg" :class="equipBg(s.status)">
                            <div class="text-2xl font-bold">{{ s.total }}</div>
                            <div class="text-xs mt-1 capitalize">{{ s.status }}</div>
                        </div>
                        <div v-if="equipmentByStatus.length === 0" class="col-span-2 text-gray-400 text-sm text-center">Aucun équipement.</div>
                    </div>
                    <div class="px-4 pb-4 flex gap-4 text-sm">
                        <span class="text-red-600 font-semibold">⚠ {{ equipmentDueMaintenance }} maintenance(s) en retard</span>
                        <span class="text-orange-500 font-semibold">📋 {{ equipmentWarrantyExpiring }} garantie(s) expirant dans 30j</span>
                    </div>
                </div>

                <!-- Plans de maintenance -->
                <div class="bg-white rounded-md shadow">
                    <div class="px-6 py-4 border-b font-semibold text-gray-700">Plans de maintenance préventive</div>
                    <div class="p-4 grid grid-cols-2 gap-3 text-center">
                        <div class="p-3 bg-gray-50 rounded-lg">
                            <div class="text-2xl font-bold text-gray-700">{{ maintenanceStats.total }}</div>
                            <div class="text-xs text-gray-500 mt-1">Total</div>
                        </div>
                        <div class="p-3 bg-green-50 rounded-lg">
                            <div class="text-2xl font-bold text-green-700">{{ maintenanceStats.active }}</div>
                            <div class="text-xs text-gray-500 mt-1">Actifs</div>
                        </div>
                        <div class="p-3 bg-orange-50 rounded-lg">
                            <div class="text-2xl font-bold text-orange-600">{{ maintenanceStats.due }}</div>
                            <div class="text-xs text-gray-500 mt-1">À exécuter</div>
                        </div>
                        <div class="p-3 bg-red-50 rounded-lg">
                            <div class="text-2xl font-bold text-red-600">{{ maintenanceStats.overdue }}</div>
                            <div class="text-xs text-gray-500 mt-1">En retard (+3j)</div>
                        </div>
                    </div>
                    <div class="px-4 pb-4 text-sm text-indigo-600">
                        📅 {{ maintenanceStats.upcoming }} plan(s) à venir dans les 30 prochains jours
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
    layout: Layout,
    components: { Head },
    props: {
        globalKpis: Object,
        ticketsByDept: Array,
        resolutionByDept: Array,
        responseByDept: Array,
        overdueTickets: Array,
        monthlyTrend: Array,
        equipmentByStatus: Array,
        equipmentDueMaintenance: Number,
        equipmentWarrantyExpiring: Number,
        maintenanceStats: Object,
    },
    computed: {
        maxDeptTotal() {
            if (!this.ticketsByDept.length) return 1
            return Math.max(...this.ticketsByDept.map(d => d.total)) || 1
        },
    },
    methods: {
        barWidth(total) {
            return Math.round((total / this.maxDeptTotal) * 100) + '%'
        },
        resolutionClass(hours) {
            if (hours <= 4)  return 'text-green-600'
            if (hours <= 24) return 'text-orange-500'
            return 'text-red-600'
        },
        equipBg(status) {
            return {
                'bg-green-50 text-green-700': status === 'operationnel',
                'bg-yellow-50 text-yellow-700': status === 'en_maintenance',
                'bg-red-50 text-red-700': status === 'en_panne' || status === 'hors_service',
                'bg-gray-50 text-gray-600': !status || status === 'Inconnu',
            }
        },
    },
}
</script>
